// DoctorShiftForm.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  PagerTargetEvent,
  HeaderThElement,
} from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { MdDelete, MdEdit } from "react-icons/md";
import {
  deleteDoctorShiftSettings,
  getDoctorShiftSettingsByDoctorID,
  insertDoctorShiftSettings,
  updateDoctorShiftSettings,
} from "./services/doctor.service";
import { clearDoctorShiftSettings } from "./doctorSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import {
  DAYS_OF_WEEK,
  SHIFT_NAMES,
  slotDurationOptions,
  STATUSARRAY,
} from "../../_contstants/common";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import FormTimePicker from "../../components/formFields/FormTimeField";
import moment from "moment";
import { Switch } from "@progress/kendo-react-inputs";
import { activeInactiveShiftSetting } from "../shiftsettings/services/shiftSettings.service";
import { getAllActiveHospitals } from "../hospital/services/hospital.service";

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorShiftForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const [editItem, setEditItem] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const shifts = useAppSelector((state) => state.doctor.DoctorShiftSettings);
  const loading = useAppSelector((state) => state.doctor.loading);
  const formLoading = useAppSelector((state) => state.doctor.formLoading);
  const HospitalList = useAppSelector((state) => state.hospital.HospitalList);
  // const BranchList = useAppSelector((state) => state.branch.BranchList);

  useEffect(() => {
    setFormKey((p) => p + 1);
  }, [shifts, editItem]);

  useEffect(() => {
    // dispatch(getAllActiveBranches());
    if (DoctorID) dispatch(getDoctorShiftSettingsByDoctorID(DoctorID));
    dispatch(getAllActiveHospitals());
    return () => {
      dispatch(clearDoctorShiftSettings());
    };
  }, [DoctorID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? shifts.length : event.page.take;
    if (targetEvent.value) setPageSizeValue(targetEvent.value);
    setPage({ ...event.page, take });
  };

  const buildPayload = (values: any) => ({
    DoctorID,
    ShiftName: values?.ShiftName,
    StartTime: values?.StartTime
      ? moment(values?.StartTime).format("HH:mm")
      : "",
    EndTime: values?.EndTime ? moment(values?.EndTime).format("HH:mm") : "",
    SlotDuration: values?.SlotDuration ? Number(values?.SlotDuration) : 0,
    BranchID: values?.BranchID ? Number(values?.BranchID) : 0,
    HospitalID: values?.HospitalID ? Number(values?.HospitalID) : null,
    ApplicableDays: Array.isArray(values?.ApplicableDays)
      ? values?.ApplicableDays.join(",")
      : values?.ApplicableDays,
    IsActive: values?.IsActive === 1,
  });

  const handleAdd = async (values: any) => {
    const response = await dispatch(
      insertDoctorShiftSettings(buildPayload(values)),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorShiftSettingsByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleUpdate = async (values: any) => {
    const response = await dispatch(
      updateDoctorShiftSettings({ ID: editItem.ID, ...buildPayload(values) }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorShiftSettingsByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await dispatch(deleteDoctorShiftSettings(deleteId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDoctorShiftSettingsByDoctorID(DoctorID));
        dispatch(closeDialog());
        setDeleteId(null);
      }
    }
  };

  const ActionCell = (props: any) => (
    <td
      className={props.className}
      style={{
        ...props.style,
        textAlign: "center",
        display: "flex",
        justifyContent: "space-evenly",
      }}
    >
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Edit"
        onClick={() =>
          setEditItem({
            ...props.dataItem,
            StartTime: props.dataItem.StartTime
              ? moment(props.dataItem.StartTime, "hh:mm").toDate()
              : "",
            EndTime: props.dataItem.EndTime
              ? moment(props.dataItem.EndTime, "hh:mm").toDate()
              : "",
            ApplicableDays: props.dataItem.ApplicableDays
              ? props.dataItem.ApplicableDays?.split(",")?.map(
                  (day: string) => day,
                )
              : "",
            IsActive: props.dataItem.IsActive ? 1 : 2,
          })
        }
        icon={MdEdit}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "20px" }}
      />
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Delete"
        className="color-red"
        onClick={() => {
          dispatch(openDialog("deleteDoctorShift"));
          setDeleteId(props.dataItem.ID);
        }}
        icon={MdDelete}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "24px" }}
      />
    </td>
  );
  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = () => {
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(activeInactiveShiftSetting(data)).then(() => {
        if (DoctorID) dispatch(getDoctorShiftSettingsByDoctorID(DoctorID));
        setIsActive(!IsActive);
      });
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.tdProps.style,
        }}
      >
        <Switch
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 6 || props.index === 7 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...(props as any).tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  return (
    <>
      {dialogName === "deleteDoctorShift" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Shift Setting</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this shift setting?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDelete}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editItem ? "Edit Shift Setting" : "Add Shift Setting"}
            </Typography.h4>
            <Form
              key={formKey}
              onSubmit={editItem ? handleUpdate : handleAdd}
              initialValues={
                editItem || {
                  ShiftName: "",
                  StartTime: "",
                  EndTime: "",
                  SlotDuration: "",
                  BranchID: "",
                  HospitalID: null,
                  ApplicableDays: "",
                  IsActive: 1,
                }
              }
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout
                    style={{ marginRight: 60 }}
                    gap={{ rows: 15, cols: 20 }}
                    cols={[
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                    ]}
                  >
                    <GridLayoutItem>
                      <Field
                        name="HospitalID"
                        label="Hospital"
                        placeholder="Select Hospital"
                        component={FormSelectionField}
                        options={HospitalList?.map((item) => ({
                          value: item.ID,
                          label: item.HospitalName,
                        }))}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="ShiftName"
                        label="Shift Name"
                        placeholder="Select shift name"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={SHIFT_NAMES}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="StartTime"
                        label="Start Time "
                        placeholder="e.g., 09:00 AM"
                        component={FormTimePicker}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem>
                      <Field
                        name="EndTime"
                        label="End Time "
                        placeholder="e.g., 06:00 PM"
                        component={FormTimePicker}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="SlotDuration"
                        label="Slot Duration"
                        placeholder="Select slot duration"
                        component={FormSelectionField}
                        options={slotDurationOptions}
                        validator={requiredValidator}
                        astrike={true}
                        wrapperStyle={{ width: "100%" }}
                      />
                    </GridLayoutItem>
                    {/* <GridLayoutItem>
                      <Field
                        name="BranchID"
                        label="Branch "
                        placeholder="Select branch"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={BranchList.map((branch: any) => ({
                          value: branch.ID,
                          label: branch.BranchName,
                        }))}
                      />
                    </GridLayoutItem> */}
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="ApplicableDays"
                        label="Applicable Days "
                        placeholder="Select applicable days"
                        component={FormMultiSelectionFiled}
                        validator={requiredValidator}
                        astrike={true}
                        options={DAYS_OF_WEEK}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="IsActive"
                        label="Status"
                        placeholder="Select status"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={STATUSARRAY?.map((status: any) => ({
                          value: status?.value,
                          label: status?.label,
                        }))}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editItem && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditItem(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editItem ? "Update Shift" : "Add Shift"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />

            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              Shift Settings ({shifts?.length || 0})
            </Typography.h4>
            {shifts && shifts.length > 0 ? (
              <KendoGrid
                className="responsive-table"
                style={{ maxHeight: "calc(100vh - 400px)" }}
                data={shifts.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={shifts.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, "All"],
                  pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{ headerCell: HeaderCustomCell, data: MyDataCustomCell }}
              >
                <Column
                  field="HospitalName"
                  title="Hospital Name"
                  width={160}
                />
                <Column field="ShiftName" title="Shift Name" width={160} />
                <Column
                  field="StartTime"
                  title="Start Time"
                  width={"120px"}
                  cells={{
                    data: (props: any) => (
                      <td>
                        {props.dataItem.StartTime
                          ? moment(props.dataItem.StartTime, "HH:mm").format(
                              "hh:mm A",
                            )
                          : ""}
                      </td>
                    ),
                  }}
                />
                <Column
                  field="EndTime"
                  title="End Time"
                  width={"120px"}
                  cells={{
                    data: (props: any) => (
                      <td>
                        {props.dataItem.EndTime
                          ? moment(props.dataItem.EndTime, "HH:mm").format(
                              "hh:mm A",
                            )
                          : ""}
                      </td>
                    ),
                  }}
                />
                <Column field="SlotDuration" title="Slot (mins)" width={110} />
                <Column field="ApplicableDays" title="Applicable Days" />
                <Column
                  field="IsActive"
                  title="Active"
                  width={90}
                  cells={{ data: MyActiveInActiveCommandCell }}
                  filterable={false}
                  locked={true}
                />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: ActionCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default DoctorShiftForm;
